import React, { useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { InclusionCategory, useGetInclusionsCategories } from '~/hooks/apis/inclusions/useGetInclusionsCategories';

import { ExtraGuestGeneratedPolicyItem, InclusionItem } from '~/types/roomPolicy';

interface RoomPolicyGuestTableProps {
  items: Array<ExtraGuestGeneratedPolicyItem>;
  onChange: (items: Array<ExtraGuestGeneratedPolicyItem>) => void;
  showValidationErrors?: boolean;
}

const GUEST_TYPE_OPTIONS = ['Adults', 'Children', 'Infants', 'Teenagers'] as const;

const FEE_PAYABLE_TO_OPTIONS = ['Paid directly to the Hotel', 'Free of Charge', 'Paid at Booking Stage'] as const;

const EMPTY_POLICY_ITEM: ExtraGuestGeneratedPolicyItem = {
  guest_type_label: '',
  age_range: '',
  fees: {
    guest_price_per_night: '',
    fee_payable_to: '',
  },
  inclusions: [],
};

export default function RoomPolicyGuestTable({
  items,
  onChange,
  showValidationErrors = false,
}: RoomPolicyGuestTableProps) {
  const { data: categories = [], isLoading: categoriesLoading } = useGetInclusionsCategories();

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => a.category.localeCompare(b.category)),
    [categories],
  );

  const categoryByIcon = useMemo(() => {
    const map = new Map<string, InclusionCategory>();
    categories.forEach((cat) => map.set(cat.icon, cat));
    return map;
  }, [categories]);

  const findCategoryByIcon = (icon: string) => categoryByIcon.get(icon) ?? null;

  const handleFieldChange = (index: number, field: string, value: string) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    onChange(updatedItems);
  };

  const handleFeeChange = (index: number, feeField: string, value: string) => {
    const updatedItems = [...items];
    updatedItems[index] = {
      ...updatedItems[index],
      fees: { ...updatedItems[index].fees, [feeField]: value },
    };
    onChange(updatedItems);
  };

  const handleInclusionTextChange = (itemIndex: number, inclusionIndex: number, value: string) => {
    const updatedItems = [...items];
    const updatedInclusions = [...updatedItems[itemIndex].inclusions];
    updatedInclusions[inclusionIndex] = {
      ...updatedInclusions[inclusionIndex],
      inclusion: value,
    };
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], inclusions: updatedInclusions };
    onChange(updatedItems);
  };

  const handleInclusionIconChange = (itemIndex: number, inclusionIndex: number, category: InclusionCategory | null) => {
    const updatedItems = [...items];
    const updatedInclusions = [...updatedItems[itemIndex].inclusions];
    updatedInclusions[inclusionIndex] = {
      ...updatedInclusions[inclusionIndex],
      icon: category?.icon ?? '',
    };
    updatedItems[itemIndex] = { ...updatedItems[itemIndex], inclusions: updatedInclusions };
    onChange(updatedItems);
  };

  const handleAddInclusion = (itemIndex: number) => {
    const updatedItems = [...items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      inclusions: [...updatedItems[itemIndex].inclusions, { inclusion: '', icon: '' }],
    };
    onChange(updatedItems);
  };

  const handleRemoveInclusion = (itemIndex: number, inclusionIndex: number) => {
    const updatedItems = [...items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      inclusions: updatedItems[itemIndex].inclusions.filter((_, i) => i !== inclusionIndex),
    };
    onChange(updatedItems);
  };

  const usedGuestTypes = new Set(items.map((item) => item.guest_type_label).filter(Boolean));
  const allGuestTypesUsed = items.length >= GUEST_TYPE_OPTIONS.length;

  const getAvailableOptions = (currentValue: string) =>
    GUEST_TYPE_OPTIONS.filter((option) => option === currentValue || !usedGuestTypes.has(option));

  const handleAddGuestType = () => {
    onChange([...items, { ...EMPTY_POLICY_ITEM, inclusions: [] }]);
  };

  const handleRemoveGuestType = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const renderInclusionsList = (item: ExtraGuestGeneratedPolicyItem, itemIndex: number) => (
    <Stack spacing={1}>
      {item.inclusions.map((inclusionItem: InclusionItem, inclusionIndex: number) => (
        <Stack key={inclusionIndex} direction="row" spacing={1} alignItems="center">
          <TextField
            fullWidth
            size="small"
            label="Inclusion"
            value={inclusionItem.inclusion}
            onChange={(e) => handleInclusionTextChange(itemIndex, inclusionIndex, e.target.value)}
          />
          <Autocomplete
            size="small"
            sx={{ minWidth: 250 }}
            options={sortedCategories}
            loading={categoriesLoading}
            value={findCategoryByIcon(inclusionItem.icon)}
            onChange={(_event, newValue) => handleInclusionIconChange(itemIndex, inclusionIndex, newValue)}
            isOptionEqualToValue={(option, value) => option.icon === value?.icon}
            groupBy={(option) => option.category}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => <TextField {...params} label="Icon" placeholder="Select icon" />}
          />
          <IconButton size="small" onClick={() => handleRemoveInclusion(itemIndex, inclusionIndex)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      <Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => handleAddInclusion(itemIndex)}>
          Add inclusion
        </Button>
      </Box>
    </Stack>
  );

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Extra Guest Policies
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 150 }}>Guest Type</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 280, width: 280 }}>Fees</TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 500 }}>Inclusions</TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 60 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index} sx={{ verticalAlign: 'top' }}>
                <TableCell>
                  <Stack spacing={1.5}>
                    <FormControl size="small" fullWidth>
                      <InputLabel id={`guest-type-label-${index}`}>Guest type</InputLabel>
                      <Select
                        labelId={`guest-type-label-${index}`}
                        label="Guest type"
                        value={item.guest_type_label}
                        onChange={(e) => handleFieldChange(index, 'guest_type_label', e.target.value)}
                      >
                        {getAvailableOptions(item.guest_type_label).map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      label="Age range"
                      required
                      value={item.age_range}
                      onChange={(e) => handleFieldChange(index, 'age_range', e.target.value)}
                      error={showValidationErrors && !item.age_range}
                      helperText={showValidationErrors && !item.age_range ? 'Age range is required' : ''}
                      fullWidth
                    />
                  </Stack>
                </TableCell>

                <TableCell>
                  <Stack spacing={1.5}>
                    <FormControl size="small" fullWidth error={showValidationErrors && !item.fees.fee_payable_to}>
                      <InputLabel id={`fee-payable-to-label-${index}`}>Fee payable to</InputLabel>
                      <Select
                        labelId={`fee-payable-to-label-${index}`}
                        label="Fee payable to"
                        required
                        value={item.fees.fee_payable_to}
                        onChange={(e) => handleFeeChange(index, 'fee_payable_to', e.target.value)}
                      >
                        {FEE_PAYABLE_TO_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {item.fees.fee_payable_to === 'Paid directly to the Hotel' && (
                      <TextField
                        size="small"
                        label="Price per night"
                        required
                        value={item.fees.guest_price_per_night}
                        onChange={(e) => handleFeeChange(index, 'guest_price_per_night', e.target.value)}
                        error={showValidationErrors && !item.fees.guest_price_per_night}
                        helperText={
                          showValidationErrors && !item.fees.guest_price_per_night ? 'Price per night is required' : ''
                        }
                        fullWidth
                      />
                    )}
                  </Stack>
                </TableCell>

                <TableCell>{renderInclusionsList(item, index)}</TableCell>

                <TableCell>
                  <IconButton size="small" onClick={() => handleRemoveGuestType(index)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {allGuestTypesUsed ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          All guest types have already been selected. Edit the fields above.
        </Typography>
      ) : (
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddGuestType} sx={{ mt: 1 }}>
          Add guest type
        </Button>
      )}
    </Box>
  );
}
